import { Button, MenuItem, useDisclosure } from "@chakra-ui/react";
import FeeOffsettableExpenseCreateModal from "features/fees/offsets/FeeOffsettableExpenseCreateModal";
import { useFeeOffsettableExpenseDestroy } from "gen/endpoints/fee-offsettable-expense/fee-offsettable-expense";
import { useFeeOffsettableExpensesTableDataRetrieveSuspense } from "gen/endpoints/fee-offsettable-expenses-table-data/fee-offsettable-expenses-table-data";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Restricted } from "shared/components/access/Restricted";
import { useConfirmationDialog } from "shared/components/modal/confirm/ConfirmationDialog";
import { DeleteConfirmationDialog } from "shared/components/modal/DeleteConfirmationDialog";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { RenderRowActionsFn } from "shared/components/table/data/DataTableTypes";
import { useHasPermission } from "shared/utils/PermissionUtils";

export interface FeeOffsettableExpensesTableProps {
  fundFamilyId: string;
}

export const FeeOffsettableExpensesTable: React.FC<
  FeeOffsettableExpensesTableProps
> = ({ fundFamilyId }) => {
  const { t } = useTranslation(["fees"]);

  const {
    data: feeOffsettableExpensesTableData,
    refetch: refetchFeeOffsettableExpensesTableData,
  } = useFeeOffsettableExpensesTableDataRetrieveSuspense(fundFamilyId);

  const { mutateAsync: deleteFeeOffsettableExpense } =
    useFeeOffsettableExpenseDestroy();

  const {
    isOpen: isCreateDialogOpen,
    onOpen: openAddDialog,
    onClose: closeAddDialog,
  } = useDisclosure();

  const onClickAddSingleOffset = useCallback(() => {
    openAddDialog();
  }, [openAddDialog]);

  const addButton = (
    <Restricted permission={PermissionTypeEnum.write}>
      <Button size="xs" onClick={onClickAddSingleOffset}>
        {t("fees:addOffset")}
      </Button>
    </Restricted>
  );

  const confirmFeeOffsettableExpenseDelete = useConfirmationDialog(
    DeleteConfirmationDialog as DeleteConfirmationDialog<string>,
    {
      title: t("fees:offsets.delete.title"),
      description: t("fees:offsets.delete.description"),
      successMessage: t("fees:offsets.delete.success"),
      performDelete: (feeOffsettableExpenseId) =>
        deleteFeeOffsettableExpense({
          fundFamilyId,
          id: feeOffsettableExpenseId,
        }),
    },
  );
  const onClickFeeOffsettableExpenseDelete = useCallback(
    async (id: string) => {
      const success = await confirmFeeOffsettableExpenseDelete(id);
      if (success) {
        await refetchFeeOffsettableExpensesTableData();
      }
    },
    [
      confirmFeeOffsettableExpenseDelete,
      refetchFeeOffsettableExpensesTableData,
    ],
  );

  const renderRowActions = useCallback<RenderRowActionsFn>(
    (props) => ({
      type: "menu",
      actions: (
        <MenuItem
          color="error.500"
          _hover={{ bg: "error.100" }}
          onClick={() => onClickFeeOffsettableExpenseDelete(props.row.id!)}
        >
          {t("fees:offsets.delete.title")}
        </MenuItem>
      ),
    }),
    [onClickFeeOffsettableExpenseDelete, t],
  );

  const hasWritePermission = useHasPermission(PermissionTypeEnum.write);

  return (
    <>
      <TableBox isFullWidth={true}>
        <TableBoxHeader>
          <TableBoxTitle>{t("fees:offsets.title")}</TableBoxTitle>
          <DataTableBoxActions createAction={addButton} />
        </TableBoxHeader>
        <TableBoxBody>
          <DataTable
            data={feeOffsettableExpensesTableData}
            emptyStateProps={{
              title: t("fees:offsets.empty.title"),
              children: addButton,
            }}
            renderRowActions={hasWritePermission ? renderRowActions : undefined}
          />
        </TableBoxBody>
      </TableBox>
      {isCreateDialogOpen && (
        <FeeOffsettableExpenseCreateModal
          fundFamilyId={fundFamilyId}
          onCloseComplete={closeAddDialog}
          onSuccess={refetchFeeOffsettableExpensesTableData}
        />
      )}
    </>
  );
};
